import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "reactstrap";

import useFetch from "Hooks/useFetch";
import { IConfig } from "Pages/Setup/Connect/connectShared";
import styles from "Pages/Setup/Connect/Onboarding.module.css";

/* ---------------------------------------------------------------------------
   Config keys this view owns.

   The mapping is keyed by Billy `requirement_id`, not by the Billy type NAME.
   A requirement_id identifies the (requirement group, type) pair and survives a
   rename in Billy, so it is the durable key. Each line is
   `IntacctType : requirement_id`, one per line — the same shape the providers
   already parse.
--------------------------------------------------------------------------- */
const VENDOR_MAP_KEY = "integration.vendor.compliance_type_mapping";
const DOC_MAP_KEY = "integration.compliance_type_mapping";

/** A requirement level the client deliberately does not sync. Comma-separated
 * requirement_ids, following the house `integration.*_to_skip` convention.
 * Split per side so "required by Billy but only maintained vendor-side" is
 * distinguishable from "not configured yet". */
const SKIP_VENDOR_KEY = "integration.billy_insurance_types_to_skip.vendor";
const SKIP_DOC_KEY = "integration.billy_insurance_types_to_skip.doc";

/** Live Billy requirement list. Not yet implemented server-side; until it is,
 * the view falls back to the requirement_ids already present in config.
 *
 * One route for both halves: the hub queues a shuttle job per provider call, so a
 * second route would double that per page view. The payload carries per-side
 * status instead. Company comes from the JWT claim, not the URL — see
 * Controllers/Setup/BillyIntacctMetadata.cs. */
const MAPPING_METADATA_ENDPOINT = "/api/billy_intacct/mapping_metadata";

type Side = "vendor" | "doc";

export interface BillyRequirement {
    requirement_id: number;
    /** Null for surveys, which carry no label in the Billy payload. */
    label: string | null;
    kind: string;
    expires?: boolean;
}

/** One Intacct compliance type as the provider returns it. `track_by` is
 * "vendor" or "primaryDocument", taken from the type's compliance definition. */
interface IntacctComplianceType {
    id: string;
    name: string;
    status?: string;
    definition_id?: string;
    track_by?: string;
}

interface ComplianceTypePools {
    vendor: IntacctComplianceType[];
    document: IntacctComplianceType[];
    /** Types whose definition could not be resolved. Surfaced rather than
     * assigned to a side, since a wrong side fails at write time. */
    unknown: IntacctComplianceType[];
}

/** The single metadata payload. The *_loaded flags are load-bearing: an empty
 * list means "genuinely nothing there" only when its flag is true. */
interface MappingMetadata {
    requirements?: BillyRequirement[];
    compliance_types?: Partial<ComplianceTypePools>;
    billy_loaded?: boolean;
    billy_error?: string | null;
    /** Groups that individually failed to read — a partial Billy result. */
    billy_groups_failed?: number;
    intacct_loaded?: boolean;
    intacct_error?: string | null;
}

/** Display order and headings for the Billy requirement kinds. All four kinds
 * are shown; team_document and survey are structurally identical to document. */
const KIND_ORDER = ["insurance", "team_document", "document", "survey"] as const;
const KIND_LABEL: Record<string, string> = {
    insurance: "Insurance",
    team_document: "Team Documents",
    document: "Documents",
    survey: "Surveys",
};

type MappingState = Record<string, Record<Side, string[]>>;

/* ---------------------------------------------------------------------------
   Parsing / serializing the flat config values
--------------------------------------------------------------------------- */

/** `IntacctType : requirement_id` per line → { [requirement_id]: [types] }. */
function parseMapping(raw: string | undefined): Record<string, string[]> {
    const out: Record<string, string[]> = {};
    (raw ?? "")
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean)
        .forEach((line) => {
            const at = line.lastIndexOf(":");
            if (at < 0) return;
            const type = line.slice(0, at).trim();
            const reqId = line.slice(at + 1).trim();
            if (!type || !reqId) return;
            if (!out[reqId]) out[reqId] = [];
            if (!out[reqId].includes(type)) out[reqId].push(type);
        });
    return out;
}

function serializeMapping(
    mapping: MappingState,
    side: Side,
    skipped: Set<string>
): string {
    const lines: string[] = [];
    Object.keys(mapping).forEach((reqId) => {
        if (skipped.has(reqId)) return;
        mapping[reqId][side].forEach((type) => lines.push(`${type} : ${reqId}`));
    });
    return lines.join("\n");
}

function parseSkip(raw: string | undefined): Set<string> {
    return new Set(
        (raw ?? "")
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
    );
}

function serializeSkip(skipped: Set<string>): string {
    return Array.from(skipped).join(",");
}

/* ------------------------------------------------------------------------- */

interface Props {
    find: (suffix: string) => IConfig | undefined;
    onSave: (suffix: string, value: string) => Promise<boolean>;
    companyId: string | undefined;
    goTo: (view: string) => void;
}

export default function ComplianceTypeMapping({
    find,
    onSave,
    companyId,
    goTo,
}: Props) {
    const { get } = useFetch();

    const vendorRaw = find(VENDOR_MAP_KEY)?.keyValue;
    const docRaw = find(DOC_MAP_KEY)?.keyValue;
    const skipVendorRaw = find(SKIP_VENDOR_KEY)?.keyValue;
    const skipDocRaw = find(SKIP_DOC_KEY)?.keyValue;

    const [requirements, setRequirements] = useState<BillyRequirement[]>([]);
    const [loadFailed, setLoadFailed] = useState(false);
    const [pools, setPools] = useState<ComplianceTypePools>({
        vendor: [],
        document: [],
        unknown: [],
    });
    const [poolsFailed, setPoolsFailed] = useState(false);
    const [loading, setLoading] = useState(true);
    const [groupsFailed, setGroupsFailed] = useState(0);
    const [mapping, setMapping] = useState<MappingState>({});
    const [skip, setSkip] = useState<Record<Side, Set<string>>>({
        vendor: new Set(),
        doc: new Set(),
    });
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);

    /* ---- load both halves of the metadata in one call ----
       Each side still degrades independently: a broken Intacct connection must not
       hide the Billy requirements, and vice versa. With a single request there is no
       per-system HTTP status to read that from, so the payload's billy_loaded /
       intacct_loaded flags are what carry it. A whole-request failure marks both
       sides failed, which is correct — nothing was read. */
    useEffect(() => {
        let ignore = false;

        (async function loadMetadata() {
            if (!companyId) return;
            setLoading(true);

            const data: MappingMetadata | null = await get(
                MAPPING_METADATA_ENDPOINT
            )
                .then(async (r) => {
                    if (!r.ok) throw new Error(String(r.status));
                    return (await r.json()) as MappingMetadata;
                })
                .catch(() => null);

            if (ignore) return;

            setRequirements(
                Array.isArray(data?.requirements) ? data!.requirements! : []
            );
            // Trust the flag, not the emptiness of the list — a client can
            // legitimately have no requirements configured in Billy yet.
            setLoadFailed(!data || data.billy_loaded === false);
            setGroupsFailed(data?.billy_groups_failed ?? 0);

            setPools({
                vendor: data?.compliance_types?.vendor ?? [],
                document: data?.compliance_types?.document ?? [],
                unknown: data?.compliance_types?.unknown ?? [],
            });
            setPoolsFailed(!data || data.intacct_loaded === false);

            setLoading(false);
        })();

        return () => {
            ignore = true;
        };
    }, [companyId, get]);

    /* ---- seed local state from config ---- */
    useEffect(() => {
        const vendorMap = parseMapping(vendorRaw);
        const docMap = parseMapping(docRaw);
        const seeded: MappingState = {};
        const touch = (reqId: string) => {
            if (!seeded[reqId]) seeded[reqId] = { vendor: [], doc: [] };
        };
        Object.keys(vendorMap).forEach((r) => {
            touch(r);
            seeded[r].vendor = vendorMap[r];
        });
        Object.keys(docMap).forEach((r) => {
            touch(r);
            seeded[r].doc = docMap[r];
        });
        requirements.forEach((r) => touch(String(r.requirement_id)));
        setMapping(seeded);
        setSkip({
            vendor: parseSkip(skipVendorRaw),
            doc: parseSkip(skipDocRaw),
        });
        setDirty(false);
    }, [vendorRaw, docRaw, skipVendorRaw, skipDocRaw, requirements]);

    /* ---- rows to render ----
       Prefer the live Billy list. When it is unavailable, fall back to the
       requirement_ids already in config so the page stays inspectable rather
       than rendering blank. */
    const rows: BillyRequirement[] = useMemo(() => {
        if (requirements.length) return requirements;
        return Object.keys(mapping)
            .sort()
            .map((reqId) => ({
                requirement_id: Number(reqId),
                label: null,
                kind: "document",
            }));
    }, [requirements, mapping]);

    const sections = useMemo(() => {
        const byKind: Record<string, BillyRequirement[]> = {};
        rows.forEach((r) => {
            const k = KIND_LABEL[r.kind] ? r.kind : "document";
            if (!byKind[k]) byKind[k] = [];
            byKind[k].push(r);
        });
        return KIND_ORDER.filter((k) => byKind[k]?.length).map((k) => ({
            kind: k as string,
            label: KIND_LABEL[k],
            items: byKind[k],
        }));
    }, [rows]);

    /** Selectable Intacct type names per side: the live pool from Intacct, plus
     * anything already mapped. A mapped name missing from the pool is kept so an
     * Intacct rename or a failed load never silently drops a saved mapping — the
     * orphan panel is what flags it. */
    const knownTypes = useMemo(() => {
        const vendor = new Set<string>(pools.vendor.map((t) => t.name));
        const doc = new Set<string>(pools.document.map((t) => t.name));
        Object.values(mapping).forEach((m) => {
            m.vendor.forEach((t) => vendor.add(t));
            m.doc.forEach((t) => doc.add(t));
        });
        return {
            vendor: Array.from(vendor).sort(),
            doc: Array.from(doc).sort(),
        };
    }, [mapping, pools]);

    /** Intacct types with no Billy requirement mapped to them — these stay
     * maintained by hand in Intacct. Only meaningful once the pool actually
     * loaded; otherwise every type would look orphaned. */
    const orphans = useMemo(() => {
        if (poolsFailed) return [];
        const usedVendor = new Set<string>();
        const usedDoc = new Set<string>();
        Object.keys(mapping).forEach((reqId) => {
            if (!skip.vendor.has(reqId)) {
                mapping[reqId].vendor.forEach((t) => usedVendor.add(t));
            }
            if (!skip.doc.has(reqId)) {
                mapping[reqId].doc.forEach((t) => usedDoc.add(t));
            }
        });
        return [
            ...pools.vendor
                .filter((t) => !usedVendor.has(t.name))
                .map((t) => ({ name: t.name, side: "vendor" })),
            ...pools.document
                .filter((t) => !usedDoc.has(t.name))
                .map((t) => ({ name: t.name, side: "document" })),
        ];
    }, [pools, poolsFailed, mapping, skip]);

    /* ---- mutations ---- */
    const addType = useCallback((reqId: string, side: Side, name: string) => {
        const type = name.trim();
        if (!type) return;
        setMapping((prev) => {
            const row = prev[reqId] ?? { vendor: [], doc: [] };
            if (row[side].includes(type)) return prev;
            const next: Record<Side, string[]> = {
                vendor:
                    side === "vendor" ? [...row.vendor, type] : row.vendor,
                doc: side === "doc" ? [...row.doc, type] : row.doc,
            };
            return { ...prev, [reqId]: next };
        });
        setDirty(true);
    }, []);

    const removeType = useCallback((reqId: string, side: Side, name: string) => {
        setMapping((prev) => {
            const row = prev[reqId];
            if (!row) return prev;
            const drop = (list: string[]) => list.filter((t) => t !== name);
            const next: Record<Side, string[]> = {
                vendor: side === "vendor" ? drop(row.vendor) : row.vendor,
                doc: side === "doc" ? drop(row.doc) : row.doc,
            };
            return { ...prev, [reqId]: next };
        });
        setDirty(true);
    }, []);

    const toggleSkipSide = useCallback((reqId: string, side: Side) => {
        setSkip((prev) => {
            const flip = (current: Set<string>) => {
                const next = new Set(current);
                if (next.has(reqId)) next.delete(reqId);
                else next.add(reqId);
                return next;
            };
            return {
                vendor: side === "vendor" ? flip(prev.vendor) : prev.vendor,
                doc: side === "doc" ? flip(prev.doc) : prev.doc,
            };
        });
        setDirty(true);
    }, []);

    const toggleSkipBoth = useCallback((reqId: string) => {
        setSkip((prev) => {
            const both = prev.vendor.has(reqId) && prev.doc.has(reqId);
            const vendor = new Set(prev.vendor);
            const doc = new Set(prev.doc);
            if (both) {
                vendor.delete(reqId);
                doc.delete(reqId);
            } else {
                vendor.add(reqId);
                doc.add(reqId);
            }
            return { vendor, doc };
        });
        setDirty(true);
    }, []);

    const save = async () => {
        setSaving(true);
        const ok =
            (await onSave(
                VENDOR_MAP_KEY,
                serializeMapping(mapping, "vendor", skip.vendor)
            )) &&
            (await onSave(
                DOC_MAP_KEY,
                serializeMapping(mapping, "doc", skip.doc)
            )) &&
            (await onSave(SKIP_VENDOR_KEY, serializeSkip(skip.vendor))) &&
            (await onSave(SKIP_DOC_KEY, serializeSkip(skip.doc)));
        setSaving(false);
        if (ok) setDirty(false);
    };

    /* ---- derived counts ---- */
    const undecided = useMemo(() => {
        let n = 0;
        rows.forEach((r) => {
            const reqId = String(r.requirement_id);
            (["vendor", "doc"] as Side[]).forEach((side) => {
                if (skip[side].has(reqId)) return;
                if (!(mapping[reqId]?.[side]?.length ?? 0)) n++;
            });
        });
        return n;
    }, [rows, mapping, skip]);

    /** Distinct Intacct types Dimension will actually maintain — the ones mapped
     * on a side that is not skipped. Counting the whole pool here would report
     * types nothing maps to as "maintained". */
    const maintainedCount = useMemo(() => {
        const maintained = new Set<string>();
        Object.keys(mapping).forEach((reqId) => {
            if (!skip.vendor.has(reqId)) {
                mapping[reqId].vendor.forEach((t) => maintained.add(`v:${t}`));
            }
            if (!skip.doc.has(reqId)) {
                mapping[reqId].doc.forEach((t) => maintained.add(`d:${t}`));
            }
        });
        return maintained.size;
    }, [mapping, skip]);

    /* ---- render helpers ---- */
    const renderCell = (r: BillyRequirement, side: Side) => {
        const reqId = String(r.requirement_id);
        const listId = `dl-${reqId}-${side}`;
        const mapped = mapping[reqId]?.[side] ?? [];

        if (skip[side].has(reqId)) {
            return (
                <div className={`${styles.mapCell} ${styles.mapCellSkipped}`}>
                    <span className={styles.mapEmpty}>skipped</span>
                    <button
                        className={styles.mapSkipLink}
                        onClick={() => toggleSkipSide(reqId, side)}
                    >
                        include
                    </button>
                </div>
            );
        }

        const pool = (side === "vendor"
            ? knownTypes.vendor
            : knownTypes.doc
        ).filter((t) => !mapped.includes(t));

        const picker = (
            <>
                <input
                    className={`${styles.mapSel} ${styles.mapInput}`}
                    list={listId}
                    placeholder="+ Intacct type…"
                    onKeyDown={(e) => {
                        if (e.key !== "Enter") return;
                        e.preventDefault();
                        const el = e.currentTarget;
                        addType(reqId, side, el.value);
                        el.value = "";
                    }}
                    onBlur={(e) => {
                        if (!e.currentTarget.value.trim()) return;
                        addType(reqId, side, e.currentTarget.value);
                        e.currentTarget.value = "";
                    }}
                />
                <datalist id={listId}>
                    {pool.map((t) => (
                        <option value={t} key={t} />
                    ))}
                </datalist>
            </>
        );

        if (!mapped.length) {
            return (
                <div className={styles.mapCell}>
                    <span className={styles.mapNoteWarn}>
                        ⚠ map or skip
                    </span>
                    {picker}
                    <button
                        className={styles.mapSkipLink}
                        onClick={() => toggleSkipSide(reqId, side)}
                    >
                        skip
                    </button>
                </div>
            );
        }

        return (
            <div className={styles.mapCell}>
                {mapped.map((t) => (
                    <span
                        className={`${styles.tchip} ${
                            side === "vendor" ? styles.tchipV : styles.tchipD
                        }`}
                        key={t}
                    >
                        {t}
                        <button
                            title="Remove"
                            onClick={() => removeType(reqId, side, t)}
                        >
                            ×
                        </button>
                    </span>
                ))}
                {picker}
                <button
                    className={styles.mapSkipLink}
                    onClick={() => toggleSkipSide(reqId, side)}
                >
                    skip
                </button>
            </div>
        );
    };

    const renderSkipMirror = (side: Side, label: string, key: string) => {
        const ids = Array.from(skip[side]);
        return (
            <div className={styles.skipCol}>
                <div className={styles.fieldHead}>
                    <label className={styles.label}>{label}</label>
                    <code className={styles.key}>{key}</code>
                </div>
                <div className={styles.skipVals}>
                    {ids.length ? (
                        ids.map((id) => (
                            <span className={styles.skipChip} key={id}>
                                {id}
                                <button
                                    title="Stop skipping"
                                    onClick={() => toggleSkipSide(id, side)}
                                >
                                    ×
                                </button>
                            </span>
                        ))
                    ) : (
                        <span className={styles.mapEmpty}>
                            nothing skipped at this level
                        </span>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className={styles.panel}>
            <div className={styles.phead}>
                <div className={`${styles.logo} ${styles.logoBilly}`}>B</div>
                <div>
                    <div className={styles.pheadName}>
                        Compliance type mapping
                    </div>
                    <div className={styles.pheadSub}>
                        Billy requirements → the Intacct compliance types
                        Dimension maintains
                    </div>
                </div>
                {!rows.length ? (
                    // No requirements and nothing mapped is "unknown", not "done" —
                    // a green pill here would claim a complete mapping that has
                    // never actually been configured.
                    <span className={`${styles.pill} ${styles.pillTodo}`}>
                        <span className={styles.dot} /> Nothing to map yet
                    </span>
                ) : undecided ? (
                    <span className={`${styles.pill} ${styles.pillWarn}`}>
                        <span className={styles.dot} /> {undecided} level
                        {undecided > 1 ? "s" : ""} need a decision
                    </span>
                ) : (
                    <span className={`${styles.pill} ${styles.pillGood}`}>
                        <span className={styles.dot} /> Mapping complete
                    </span>
                )}
            </div>

            <div className={styles.sec}>
                <p className={styles.hint}>
                    Each Billy compliance requirement is keyed by its{" "}
                    <span className={styles.mono}>requirement_id</span> — a
                    stable identifier that survives a rename in Billy. Intacct
                    has two kinds of compliance type, decided by the compliance
                    definition's <b>Track by</b> setting: vendor-level and
                    document-level (project / subcontract). One Billy
                    requirement can feed several Intacct types on either side.
                </p>

                <div className={styles.banner}>
                    Intacct <b>compliance type IDs are unique company-wide</b> —
                    a vendor-level and a document-level type cannot share a
                    name. Suffix the project-level ones (
                    <span className={styles.mono}>General Liability</span> vs{" "}
                    <span className={styles.mono}>
                        General Liability - Project
                    </span>
                    ).
                </div>

                {loading && (
                    <p className={styles.hint}>
                        Loading Billy requirements and Intacct compliance types…
                    </p>
                )}

                {!loading && loadFailed && (
                    <div className={styles.banner}>
                        Could not load the live Billy requirement list. The rows
                        below are derived from the requirement_ids already saved
                        in config, so existing mappings stay editable — but new
                        requirements will not appear until the load succeeds.
                        Check the Billy connection on <b>1 · Connect systems</b>.
                    </div>
                )}

                {!loading && !loadFailed && groupsFailed > 0 && (
                    <div className={styles.banner}>
                        {groupsFailed} Billy requirement group
                        {groupsFailed > 1 ? "s" : ""} could not be read, so the
                        list below may be incomplete. Requirements from the groups
                        that did load are shown and are safe to map.
                    </div>
                )}

                {!loading && poolsFailed && (
                    <div className={styles.banner}>
                        Could not load Intacct compliance types. You can still
                        type a type name by hand, but it will not be checked
                        against Intacct and a typo will be dropped with a warning
                        at run time. Check the Sage Intacct connection on{" "}
                        <b>1 · Connect systems</b>.
                    </div>
                )}

                {!loading && !poolsFailed && pools.unknown.length > 0 && (
                    <div className={styles.banner}>
                        {pools.unknown.length} Intacct compliance type
                        {pools.unknown.length > 1 ? "s have" : " has"} no
                        resolvable <span className={styles.mono}>Track by</span>{" "}
                        setting, so {pools.unknown.length > 1 ? "they are" : "it is"}{" "}
                        not offered on either side:{" "}
                        <span className={styles.mono}>
                            {pools.unknown.map((t) => t.name).join(", ")}
                        </span>
                        . Fix the compliance definition in Intacct to use{" "}
                        {pools.unknown.length > 1 ? "them" : "it"} here.
                    </div>
                )}

                <div className={styles.mapLegend}>
                    <span>
                        <i
                            className={styles.mapSwatch}
                            style={{ background: "#1f3ea8" }}
                        />{" "}
                        Billy requirement (source)
                    </span>
                    <span>
                        <i
                            className={styles.mapSwatch}
                            style={{ background: "#198754" }}
                        />{" "}
                        Intacct vendor-level · Track by = Vendor
                    </span>
                    <span>
                        <i
                            className={styles.mapSwatch}
                            style={{ background: "#fd7e14" }}
                        />{" "}
                        Intacct document-level · Track by = Primary document
                    </span>
                </div>

                <p className={styles.help}>
                    Levels excluded from the sync, <b>per side</b> — a
                    requirement can be skipped vendor-side and still maintained
                    at document level. Set these with the <b>skip</b> links on
                    the rows; the boxes below mirror the resulting config value.
                </p>
                <div className={styles.skipGrid}>
                    {renderSkipMirror(
                        "vendor",
                        "Skipped — vendor level",
                        SKIP_VENDOR_KEY
                    )}
                    {renderSkipMirror(
                        "doc",
                        "Skipped — document level",
                        SKIP_DOC_KEY
                    )}
                </div>

                <div className={styles.mapHead}>
                    <div
                        className={`${styles.mapHeadCell} ${styles.mapHeadBilly}`}
                    >
                        Billy compliance type
                        <small>requirement_id</small>
                    </div>
                    <div
                        className={`${styles.mapHeadCell} ${styles.mapHeadVendor}`}
                    >
                        Intacct — vendor level
                        <small>Track by = Vendor</small>
                    </div>
                    <div
                        className={`${styles.mapHeadCell} ${styles.mapHeadDoc}`}
                    >
                        Intacct — document level
                        <small>Track by = Primary document</small>
                    </div>
                </div>

                {!rows.length ? (
                    <div className={styles.sec}>
                        <span className={styles.mapEmpty}>
                            No Billy requirements loaded and nothing mapped yet.
                        </span>
                    </div>
                ) : (
                    sections.map((section) => (
                        <div key={section.kind}>
                            <div className={styles.mapGroup}>
                                {section.label}
                                <span className={styles.mapGroupCount}>
                                    {section.items.length}
                                </span>
                            </div>
                            {section.items.map((r) => {
                                const reqId = String(r.requirement_id);
                                const skippedBoth =
                                    skip.vendor.has(reqId) &&
                                    skip.doc.has(reqId);
                                const needsDecision =
                                    !skippedBoth &&
                                    (["vendor", "doc"] as Side[]).some(
                                        (side) =>
                                            !skip[side].has(reqId) &&
                                            !(
                                                mapping[reqId]?.[side]
                                                    ?.length ?? 0
                                            )
                                    );

                                if (skippedBoth) {
                                    return (
                                        <div
                                            className={`${styles.mapRow} ${styles.mapRowSkipped}`}
                                            key={reqId}
                                        >
                                            <div className={styles.mapSrc}>
                                                {r.label ?? "(untitled)"}
                                                <small>
                                                    ID {reqId} · skipped both
                                                    levels
                                                </small>
                                            </div>
                                            <div
                                                className={`${styles.mapCell} ${styles.mapSkipCell}`}
                                            >
                                                <span
                                                    className={styles.mapEmpty}
                                                >
                                                    Not synced at either level
                                                </span>
                                                <Button
                                                    size="sm"
                                                    color="link"
                                                    onClick={() =>
                                                        toggleSkipBoth(reqId)
                                                    }
                                                >
                                                    Include
                                                </Button>
                                            </div>
                                        </div>
                                    );
                                }

                                return (
                                    <div
                                        className={`${styles.mapRow} ${
                                            needsDecision
                                                ? styles.mapRowWarn
                                                : ""
                                        }`}
                                        key={reqId}
                                    >
                                        <div className={styles.mapSrc}>
                                            {r.label ?? "(untitled)"}
                                            <small>
                                                ID {reqId}
                                                {r.expires === true
                                                    ? " · expires"
                                                    : r.expires === false
                                                    ? " · no expiration"
                                                    : ""}{" "}
                                                ·{" "}
                                                <button
                                                    className={
                                                        styles.mapSkipLink
                                                    }
                                                    onClick={() =>
                                                        toggleSkipBoth(reqId)
                                                    }
                                                >
                                                    skip both
                                                </button>
                                            </small>
                                        </div>
                                        {renderCell(r, "vendor")}
                                        {renderCell(r, "doc")}
                                    </div>
                                );
                            })}
                        </div>
                    ))
                )}

                <div className={styles.orphanBox}>
                    <div className={styles.orphanHead}>
                        What gets written to config
                    </div>
                    <p className={styles.mapNote} style={{ margin: "0 0 10px" }}>
                        One line per mapped type,{" "}
                        <span className={styles.mono}>
                            IntacctType : requirement_id
                        </span>
                        . A skipped level is omitted from its key entirely, which
                        is what separates “deliberately not synced” from “not
                        configured yet”.
                    </p>
                    <div className={styles.skipGrid} style={{ margin: 0 }}>
                        <div className={styles.skipCol}>
                            <div className={styles.fieldHead}>
                                <code className={styles.key}>
                                    {VENDOR_MAP_KEY}
                                </code>
                            </div>
                            <div className={styles.serialBlock}>
                                {serializeMapping(
                                    mapping,
                                    "vendor",
                                    skip.vendor
                                ) || (
                                    <span className={styles.mapEmpty}>
                                        empty — nothing maintained at this level
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className={styles.skipCol}>
                            <div className={styles.fieldHead}>
                                <code className={styles.key}>{DOC_MAP_KEY}</code>
                            </div>
                            <div className={styles.serialBlock}>
                                {serializeMapping(mapping, "doc", skip.doc) || (
                                    <span className={styles.mapEmpty}>
                                        empty — nothing maintained at this level
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {!poolsFailed && (
                    <div className={styles.orphanBox}>
                        <div className={styles.orphanHead}>
                            Intacct types with no Billy source
                        </div>
                        <div className={styles.orphanList}>
                            {orphans.length ? (
                                orphans.map((o) => (
                                    <span
                                        className={styles.orphanChip}
                                        key={`${o.side}:${o.name}`}
                                    >
                                        {o.name}
                                        <i>{o.side}</i>
                                    </span>
                                ))
                            ) : (
                                <span className={styles.mapNoteOk}>
                                    ✓ every Intacct compliance type has a Billy
                                    source
                                </span>
                            )}
                        </div>
                        <p className={styles.mapNote} style={{ margin: "10px 0 0" }}>
                            Dimension will not touch these — they stay maintained
                            by hand in Intacct.
                        </p>
                    </div>
                )}
            </div>

            <div className={styles.footbar}>
                <Button color="link" onClick={() => goTo("intacct")}>
                    ← Back to Sage Intacct
                </Button>
                <Button
                    color="primary"
                    disabled={!dirty || saving}
                    onClick={save}
                >
                    {saving ? "Saving…" : "Save mapping"}
                </Button>
                <span className={styles.saveState}>
                    {rows.length} requirement
                    {rows.length === 1 ? "" : "s"} · {maintainedCount} Intacct
                    type{maintainedCount === 1 ? "" : "s"} maintained ·{" "}
                    {skip.vendor.size + skip.doc.size} level-skip
                    {skip.vendor.size + skip.doc.size === 1 ? "" : "s"}
                    {dirty ? " · unsaved changes" : ""}
                </span>
            </div>
        </div>
    );
}
